import React from "react";
import FontAwesome from "react-fontawesome";
import { CSS } from "@dnd-kit/utilities";
import { useSortable } from "@dnd-kit/sortable";
import { NESTED_PADDING, ROOT_PARENT } from "../utilities/dragUtils";
import { RowGripIcon } from "./RowGripIcon";
import { RowActionsMenu } from "./RowActionsMenu";

/** @typedef {import("../types").InfoNode} InfoNode */

/**
 * Renders a sortable row for a page (root-level or nested).
 * @param {{
 *   node: InfoNode,
 *   canAdmin: boolean,
 *   onDeleteNode: () => void,
 *   onEditNode: (nodeId: string) => void,
 * }} props
 */
export function SortablePageRow({ node, canAdmin, onDeleteNode, onEditNode }) {
	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
		isOver,
	} = useSortable({
		id: node.RowId,
		data: { nodeType: "page", parentId: node.parentId || ROOT_PARENT },
	});

	const isNested = node.parentId && node.parentId !== ROOT_PARENT;
	const style = {
		opacity: isDragging ? 0.85 : 1,
		transform: CSS.Transform.toString(transform),
		transition,
		marginLeft: isNested ? NESTED_PADDING : 0,
	};

	const className = [
		"infopage__row",
		isNested ? "infopage__row--nested" : "",
		isOver ? "infopage__row--drop-target" : "",
		isDragging ? "infopage__row--dragging" : "",
	]
		.filter(Boolean)
		.join(" ");

	const actionItems = canAdmin
		? [
				{
					label: "Edit page",
					icon: "pencil",
					onSelect: () => onEditNode && onEditNode(node.RowId),
				},
				{
					label: "Delete page",
					icon: "trash",
					onSelect: onDeleteNode,
				},
			]
		: [];

	return (
		<div
			ref={setNodeRef}
			style={style}
			className={className}
			data-node-type="page"
			data-node-parent={isNested ? "folder" : ROOT_PARENT}
			data-dragging={isDragging ? "true" : undefined}
		>
			{isNested && <div className="row__folder-lines" />}
			<div className="row__drag-handle" {...listeners} {...attributes}>
				<RowGripIcon />
			</div>
			<div className="row__content">
				<div className="row__primary">
					<FontAwesome name="file-text-o" className="row__type-icon" />
					<span className="row__title">{node.title}</span>
				</div>
			</div>
			{actionItems.length ? (
				<RowActionsMenu
					label={`More actions for ${node.title}`}
					items={actionItems}
				/>
			) : null}
		</div>
	);
}
